import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router'
import { PasswordInput } from '@/components/password-input'
import { Button } from '@/components/ui/button'
import { Field, FieldError, FieldGroup, FieldLabel } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { useAnalytics } from '@/hooks/use-analytics'
import { useToast } from '@/hooks/use-toast'
import { useForm } from '@tanstack/react-form'
import { AlertCircle, KeyRound, Loader2, Mail, Sparkles } from 'lucide-react'
import type React from 'react'
import { z } from 'zod'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { isSafeRedirect } from '@/lib/url-validation'
import { magicLinkAuthMagicLinkPostMutation } from '@/client/@tanstack/react-query.gen'
import { useMutation } from '@tanstack/react-query'
import { parseApiError } from '@/lib/parse-api-error'

interface LoginSearch {
  redirect?: string
  error?: string
}

export const Route = createFileRoute('/_login_forms/login')({
  validateSearch: (search: Record<string, unknown>): LoginSearch => ({
    redirect: search['redirect'] as string | undefined, // eslint-disable-line dot-notation
    error: search['error'] as string | undefined, // eslint-disable-line dot-notation
  }),
  beforeLoad: ({ context, search }) => {
    if (context.auth.isAuthenticated && !context.auth.isLoading) {
      throw redirect({ to: isSafeRedirect(search.redirect) })
    }
    return {
      title: 'Welcome',
      description: 'Sign in to SCOUT',
    }
  },
  component: LoginForm,
})

/**
 * LoginForm supports:
 * - Password login (validates password on submit)
 * - Magic link (sends link if valid email entered, or routes to magic link page if not)
 * - Displays error from URL if present
 * Validation: email required and validated live; password required only for password login
 */
function LoginForm() {
  const navigate = useNavigate()
  const { toast } = useToast()
  const { capture } = useAnalytics()
  const { auth } = Route.useRouteContext()
  const { redirect, error } = Route.useSearch()

  const sendMagicLink = useMutation({
    ...magicLinkAuthMagicLinkPostMutation(),
    onSuccess: (_data, variables) => {
      capture('auth_magic_link_requested', { source: 'login_form' })
      navigate({
        to: '/magic-link-sent',
        search: { email: variables.body.email },
      })
    },
    onError: (error) => {
      toast({ title: parseApiError(error), variant: 'destructive' })
    },
  })
  const form = useForm({
    defaultValues: { email: '', password: '' },
    validators: {
      onChange: ({ value }) => {
        const schema = z.object({
          email: z.string().email(),
          password: z.string(),
        })
        const result = schema.safeParse(value)
        return result.success
          ? undefined
          : result.error.issues.map(index => index.message).join(', ')
      },
      onSubmit: ({ value }) => {
        // Only validate password on submit (when user is trying to login, not magic link)
        return value.password ? undefined : 'Password is required'
      },
    },
    onSubmit: async ({ value }) => {
      try {
        const user = await auth.login(value.email, value.password)
        // Redirect superusers to admin portal, others to home or redirect URL
        const destination = user.is_superuser ? '/admin' : isSafeRedirect(redirect)
        navigate({ to: destination })
      }
      catch (error: any) {
        let error_kind = 'unknown'
        if (typeof error?.detail === 'string') {
          error_kind = error.detail
        }
        else if (error instanceof Error) {
          error_kind = 'exception'
        }
        capture('auth_login_failed', { error_kind })
        if (error?.detail) {
          const detail = typeof error.detail === 'string' ? error.detail : error.detail
          if (detail === 'LOGIN_BAD_CREDENTIALS') {
            toast({
              title: 'Invalid credentials',
              description: 'The email or password you entered is incorrect. Please try again.',
              variant: 'destructive',
            })
          }
          else {
            toast({
              title: typeof detail === 'string' ? detail : 'An error occurred',
              variant: 'destructive',
            })
          }
        }
        else if (error instanceof Error) {
          toast({ title: error.message, variant: 'destructive' })
        }
        else {
          toast({ title: 'An unexpected error occurred', variant: 'destructive' })
        }
      }
    },
  })

  function handleMagicLinkClick() {
    const email = form.getFieldValue('email')
    // If email is valid, send magic link directly.
    if (email && z.string().email().safeParse(email).success) {
      sendMagicLink.mutate({
        body: {
          email,
        },
      })
      return
    }

    navigate({ to: '/magic-link' })
  }

  return (
    <div>
      <form
        id="login-form"
        className="space-y-6"
        onSubmit={(event) => {
          event.preventDefault()
          form.handleSubmit()
        }}
      >
        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              {error === 'auth_failed'
                ? 'Authentication failed. Please try again.'
                : error}
            </AlertDescription>
          </Alert>
        )}
        <FieldGroup>
          {/* Email Input */}
          <form.Field
            name="email"
            children={(field) => {
              const isInvalid = field.state.meta.isTouched && !field.state.meta.isValid

              return (
                <Field data-invalid={isInvalid}>
                  <FieldLabel htmlFor="email" className="text-base">Email</FieldLabel>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      id="email"
                      name={field.name}
                      type="email"
                      autoComplete="email"
                      placeholder="you@example.com"
                      value={field.state.value}
                      onBlur={field.handleBlur}
                      onChange={event_ => field.handleChange(event_.target.value)}
                      aria-invalid={isInvalid}
                      disabled={form.baseStore.state.isSubmitting}
                      className="pl-12"
                      required
                    />
                  </div>
                  {isInvalid && <FieldError errors={field.state.meta.errors} />}
                </Field>
              )
            }}
          />

          {/* Password Input */}
          <form.Field
            name="password"
            children={(field) => {
              const isInvalid = field.state.meta.isTouched && !field.state.meta.isValid

              return (
                <Field data-invalid={isInvalid}>
                  <FieldLabel htmlFor="password" className="text-base">Password</FieldLabel>
                  <div className="relative">
                    <KeyRound className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                    <PasswordInput
                      id="password"
                      name={field.name}
                      autoComplete="current-password"
                      placeholder="••••••••"
                      value={field.state.value}
                      onBlur={field.handleBlur}
                      onChange={(event_: React.ChangeEvent<HTMLInputElement>) => field.handleChange(event_.target.value)}
                      aria-invalid={isInvalid}
                      disabled={form.baseStore.state.isSubmitting}
                      className="pl-12"
                    />
                  </div>
                  <a
                    href="/forgot-password"
                    className="text-sm underline-offset-2 hover:underline"
                  >
                    Forgot your password?
                  </a>
                  {isInvalid && <FieldError errors={field.state.meta.errors} />}
                </Field>
              )
            }}
          />
        </FieldGroup>

        {/* Sign In Button */}
        <Button
          type="submit"
          form="login-form"
          variant="default"
          className="w-full"
          disabled={form.baseStore.state.isSubmitting}
        >
          {form.baseStore.state.isSubmitting
            ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Signing in...
                </>
              )
            : (
                'Sign in with email'
              )}
        </Button>

        {/* OR Divider */}
        <div className="relative py-2">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t" />
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-background px-3 text-muted-foreground">
              Or
            </span>
          </div>
        </div>

        {/* Send Magic Link Button */}
        <Button
          type="button"
          variant="outline"
          className="w-full"
          onClick={handleMagicLinkClick}
          disabled={form.baseStore.state.isSubmitting || sendMagicLink.isPending}
        >
          {sendMagicLink.isPending
            ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Sending magic link...
                </>
              )
            : (
                <>
                  <Sparkles className="mr-2 h-5 w-5" />
                  Send magic link instead
                </>
              )}
        </Button>
      </form>
    </div>
  )
}
